import React from 'react';
import { pdf } from '@react-pdf/renderer';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from './firebase';
import { getPublicSettings } from './appSettings';
import ChangeOrderPdfDocument from '../components/proposals/ChangeOrderPdfDocument';
import type { ChangeOrder } from '../types';

export async function fetchChangeOrderLogoBase64(): Promise<string | undefined> {
  try {
    const settings = await getPublicSettings();
    if (settings.engagementLogo?.url) {
      const url = settings.engagementLogo.url.includes('?')
        ? `${settings.engagementLogo.url}&t=${Date.now()}`
        : `${settings.engagementLogo.url}?t=${Date.now()}`;
      const response = await fetch(url, { mode: 'cors', cache: 'no-store', credentials: 'omit' });
      if (response.ok) {
        const blob = await response.blob();
        return await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = () => reject(new Error('FileReader failed'));
          reader.readAsDataURL(blob);
        });
      }
    }
  } catch {
    /* fall through */
  }
  try {
    const fallback = await fetch('/assets/logo/LC_pdf_logo.png');
    if (fallback.ok) {
      const blob = await fallback.blob();
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('FileReader failed'));
        reader.readAsDataURL(blob);
      });
    }
  } catch {
    /* ignore */
  }
  return undefined;
}

/** Generate and upload an approved change order PDF; returns the storage URL or null. */
export async function uploadApprovedChangeOrderPdf(
  co: ChangeOrder,
  priorContractValueFallback: number,
): Promise<string | null> {
  if (!co.id) return null;
  const priorContractValue =
    typeof co.contractValueBefore === 'number' && Number.isFinite(co.contractValueBefore)
      ? co.contractValueBefore
      : priorContractValueFallback;
  try {
    const settings = await getPublicSettings();
    const logo = await fetchChangeOrderLogoBase64();
    const blob = await pdf(
      <ChangeOrderPdfDocument
        changeOrder={co}
        companyLogo={logo}
        companyName={settings.companyName}
        companyWebsite={settings.companyWebsite}
        priorContractValue={priorContractValue}
      />,
    ).toBlob();
    const path = `signed-change-orders/${co.dealId}/${co.id}-signed.pdf`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, blob);
    return await getDownloadURL(storageRef);
  } catch (err) {
    console.warn('[changeOrderPdf] upload failed', err);
    return null;
  }
}
